import axios from "axios";
import { useState } from "react";
import { Modal } from "react-bootstrap";
import toastr from "toastr";
import { CustomDropdown } from "./CustomDropdown";
import {
  paTypeFlags,
  paTypeOf,
  PaTypeSelection,
} from "../enroll/policy/pa-setting-helpers";
import "../styles/CustomComponents.css";

const PA_TYPE_CHOICES: { value: PaTypeSelection; label: string }[] = [
  { value: "none", label: "None" },
  { value: "group", label: "Group PA" },
  { value: "individual", label: "Individual PA" },
];

const EditPlanAdminSettingModal = ({policyId, paData, updatePA}) => {
    const [show, setShow] = useState(false);
    const [hideOnEE, setHideOnEE] = useState(paData.hideOnEE);
    const [receiveEmail, setReceiveEmail] = useState(paData.receiveEmail);
    const [paType, setPaType] = useState<PaTypeSelection>(paTypeOf(paData));
    const [role, setRole] = useState(paData.role);
    const [showCustomCatReport, setShowCustomCatReport] = useState(paData.showCustomCatReport);
    const [twofactor, setTwoFactor] = useState(paData.twoFactorEnabled || false);
    const [isTrained, setIsTrained] = useState(paData.isTrained || false);
    const [readOnly, setIsReadOnly] = useState(paData.readOnly || false);

    const handleShow = () => {
      setHideOnEE(paData.hideOnEE);
      setReceiveEmail(paData.receiveEmail);
      setPaType(paTypeOf(paData));
      setRole(paData.role);
      setShowCustomCatReport(paData.showCustomCatReport);
      setTwoFactor(paData.twoFactorEnabled || false);
      setIsTrained(paData.isTrained || false);
      setIsReadOnly(paData.readOnly || false);
      setShow(true);
    };
    const handleClose = () => setShow(false);

    const handleSave = () => {
      const flags = paTypeFlags(paType);
      const data = {
        PolicyId: policyId,
        PlanAdminUserId: paData.id,
        HideOnEE: hideOnEE,
        ReceiveEmail: receiveEmail,
        SendGroupPAEmail: flags.sendGroupPAEmail,
        SendIndividualPAEmail: flags.sendIndividualPAEmail,
        Role: role,
        ShowCustomCatReport: showCustomCatReport,
        TwoFactor: twofactor,
        IsTrained: isTrained,
        ReadOnly: readOnly
      };
      axios
        .put("/api/assignplanadmin/updatepasetting", data)
        .then((res) => {
          if (res.data.success) {
            toastr.success("Plan Admin Setting Updated");
            updatePA();
            handleClose();
          } else {
            toastr.error(res.data.message || "Error updating plan admin settings.");
          }
        })
        .catch((err) => {
          toastr.error("An error occurred while updating settings.");
          console.error("Error saving plan admin settings:", err);
        });
    };

    const handleShowCustomCatReport = (event) => {
        setShowCustomCatReport(event.target.checked);
    };

    const handleTwoFactorAuthentication = (event) => {
      setTwoFactor(event.target.checked);
    };    

    const handleTrainedChange = (event) => {
      const { checked } = event.target;
      setIsTrained(checked);
    };

     const handleReadOnlyChange = (event) => {
      const { checked } = event.target;
      setIsReadOnly(checked);
    };


    const uniqueIdSuffix = paData.id || Date.now();

    return (
      <>
        <a href="#" className="RT-Link" onClick={handleShow}>
          Edit
        </a>
        <Modal show={show} onHide={handleClose} className="edit-planadmin-setting-modal">
          <Modal.Header>
            <Modal.Title style={{fontSize: '17px'}}>Edit Plan Admin</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="pa-fieldset">
              <div className="pa-fieldset__legend">General</div>
              <div className="pa-field-grid">
                <div className="checkbox-container">
                  <input type="checkbox"
                    id={`hideOnEE-${uniqueIdSuffix}`}
                    checked={hideOnEE}
                    onChange={(e) => setHideOnEE(e.target.checked)}
                  />
                  <label htmlFor={`hideOnEE-${uniqueIdSuffix}`}>Hide on EE Portal</label>
                </div>
                <div className="checkbox-container">
                  <input type="checkbox"
                    id={`receiveEmail-${uniqueIdSuffix}`}
                    checked={receiveEmail}
                    onChange={(e) => setReceiveEmail(e.target.checked)}
                  />
                  <label htmlFor={`receiveEmail-${uniqueIdSuffix}`}>Receive Email</label>
                </div>
                <div className="checkbox-container">
                  <input type="checkbox"
                    id={`twoFactor-${uniqueIdSuffix}`}
                    checked={twofactor}
                    onChange={handleTwoFactorAuthentication}
                  />
                  <label htmlFor={`twoFactor-${uniqueIdSuffix}`}>2 Factor Authentication</label>
                </div>
                <div className="checkbox-container">
                  <input type="checkbox"
                    id={`isTrained-${uniqueIdSuffix}`}
                    checked={isTrained}
                    onChange={handleTrainedChange}
                  />
                  <label htmlFor={`isTrained-${uniqueIdSuffix}`}>Trained?</label>
                </div>
              </div>
            </div>

            <div className="pa-fieldset">
              <div className="pa-fieldset__legend">PA Type</div>
              <div className="pa-field-grid">
                <div>
                  {PA_TYPE_CHOICES.map((choice) => (
                    <div className="radio-container" key={choice.value}>
                      <input type="radio"
                        id={`paType-${choice.value}-${uniqueIdSuffix}`}
                        name={`paType-${uniqueIdSuffix}`}
                        value={choice.value}
                        checked={paType === choice.value}
                        onChange={() => setPaType(choice.value)}
                      />
                      <label htmlFor={`paType-${choice.value}-${uniqueIdSuffix}`}>{choice.label}</label>
                    </div>
                  ))}
                </div>
                <div className="pa-role-field">
                  <label>Role</label>
                  <CustomDropdown
                    options={["--Select--", "Primary", "Secondary"]}
                    selectedOption={role == null ? "--Select--" : role}
                    setSelectedOption={(val) => {
                      if (val === "--Select--") {
                        val = null;
                      }
                      setRole(val);
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="pa-fieldset">
              <div className="pa-fieldset__legend">Permissions</div>
              <div className="checkbox-container">
                <input type="checkbox"
                  id={`showCustomCatReport-${uniqueIdSuffix}`}
                  checked={showCustomCatReport}
                  onChange={handleShowCustomCatReport}
                />
                <label htmlFor={`showCustomCatReport-${uniqueIdSuffix}`}>Show Enhanced Benefit Report</label>
              </div>
              <div className="checkbox-container">
                <input type="checkbox"
                  id={`readOnly-${uniqueIdSuffix}`}
                  checked={readOnly}
                  onChange={handleReadOnlyChange}
                />
                <label htmlFor={`readOnly-${uniqueIdSuffix}`}>Read Only</label>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button className="btn btn-secondary" onClick={handleClose}>
              Close
            </button>
            <button className="btn btn-primary" onClick={handleSave}>
              Save
            </button>
          </Modal.Footer>
        </Modal>
      </>
    );
  };
  
  export default EditPlanAdminSettingModal;